"use client";

import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { FunctionSquare } from "lucide-react";
import { Variable } from "lucide-react";
import { Undo, Redo, Save, Upload, Copy, Trash2, FileText } from "lucide-react";
import type { SilaeVariableType } from "@/types/silae";
import type { RootState } from "@/store";
import { useAppSelector } from "@/hooks/redux";
import { Tooltip, TooltipProvider } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { useFeatures } from "@/hooks/useFeatures";
import { useSilaeVariables } from "@/hooks/useSilaeVariables";
import {
  syntaxColors,
  completionDatabase,
  getSyntaxPatterns,
  getCurrentWord,
  getFilteredCompletions,
  applySyntaxHighlighting,
  adjustClosingKeywordIndentation,
  insertCompletion,
  handleEnterKey,
  handleTabKey,
  type CompletionItem,
  extendedCompletionDatabase,
} from "../../utils/silae-editor-utils";

interface SilaeCodeEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  readOnly?: boolean;
  fileName?: string;
  onFileNameChange?: (fileName: string) => void;
  onRunCode?: () => void;
  onSaveCode?: () => void;
  onLoadFile?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onCopyCode?: () => void;
  onClearCode?: () => void;
  onLoadExample?: () => void;
  isInDialog?: boolean; // Nouvelle prop pour contrôler le positionnement des tooltips
}

/**
 * Éditeur de code SILAE avec coloration syntaxique personnalisée
 */
export const SilaeCodeEditor: React.FC<SilaeCodeEditorProps> = ({
  value,
  onChange,
  placeholder,
  className,
  readOnly = false,
  fileName,
  onFileNameChange,
  onSaveCode,
  onLoadFile,
  onCopyCode,
  onClearCode,
  onLoadExample,
  isInDialog = false,
}) => {
  const { t } = useTranslation();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const highlightRef = useRef<HTMLDivElement>(null);
  const lineNumbersRef = useRef<HTMLDivElement>(null);
  const [highlightedContent, setHighlightedContent] = useState<
    React.ReactElement[]
  >([]);
  const [showCompletions, setShowCompletions] = useState(false);
  const [completions, setCompletions] = useState<CompletionItem[]>([]);
  const [selectedCompletionIndex, setSelectedCompletionIndex] = useState(0);
  const [completionPosition, setCompletionPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  // Système d'undo/redo
  const [history, setHistory] = useState<string[]>([value]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [isUndoRedo, setIsUndoRedo] = useState(false);
  const [isCompletionInsertion, setIsCompletionInsertion] = useState(false);
  const historyTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { user } = useAppSelector((state: RootState) => state.auth);

  // Charger les variables Silae depuis Redux
  const { variables: silaeVariables } = useSilaeVariables();

  // Utiliser les traductions par défaut si les props ne sont pas fournies
  const defaultPlaceholder = placeholder || t("silaeCodeEditor.placeholder");
  const defaultFileName = fileName || t("silaeCodeEditor.defaultFileName");
  const { hasFeature } = useFeatures();

  const hasFileManagement = hasFeature("export_functionality");

  const handleSaveCode =
    onSaveCode ||
    (() => {
      const blob = new Blob([value], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = defaultFileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(t("silaeCodeEditor.messages.codeSaved"));
    });

  const handleCopyCode =
    onCopyCode ||
    (() => {
      navigator.clipboard.writeText(value);
      toast.success(t("silaeCodeEditor.messages.codeCopied"));
    });

  const handleClearCode =
    onClearCode ||
    (() => {
      onChange("Begin\n  \nEnd");
      toast.success(t("silaeCodeEditor.messages.codeCleared"));
    });

  const handleLoadExample =
    onLoadExample ||
    (() => {
      const exampleCode = `Begin
  // ${t("silaeCodeEditor.exampleCode.comment")}
  If bul.mois = 12 Then
    Exec("Lprime_D05")
    methodeCalcul = 2
    
    // ${t("silaeCodeEditor.exampleCode.baseCalculation")}
    datedeb = Date(1,1,bul.annee)
    datefin = Date(31,12,bul.annee)
    SalBase = cumulbulletins(datedeb,datefin,0)
    Bases = SalBase / 12
    
    // ${t("silaeCodeEditor.exampleCode.forceBase")}
    BaseForce = Saisie("13Mois",0)
    If BaseForce > 0 Then Bases = BaseForce
    If BaseForce < 0 Then Bases = 0
    
    Liblong = "${t("silaeCodeEditor.exampleCode.libelle")}"
    
    // ${t("silaeCodeEditor.exampleCode.generation")}
    If Bases <> 0 Then Exec("GenereLprime")
    
    // ${t("silaeCodeEditor.exampleCode.storage")}
    call stockevar("Pr_13Mois",Bases)
  Endif
End`;
      onChange(exampleCode);
      toast.success(t("silaeCodeEditor.messages.exampleLoaded"));
    });

  // Fonction pour calculer la position du curseur et mettre à jour le popover
  const updateCompletionPosition = useCallback(
    (textarea: HTMLTextAreaElement) => {
      const cursorPosition = textarea.selectionStart;
      const textBeforeCursor = value.substring(0, cursorPosition);
      const lines = textBeforeCursor.split("\n");
      const currentLine = lines.length - 1;

      // Créer un élément de test pour mesurer les caractères
      const measureDiv = document.createElement("div");
      measureDiv.style.position = "absolute";
      measureDiv.style.visibility = "hidden";
      measureDiv.style.whiteSpace = "pre";
      measureDiv.style.font = window.getComputedStyle(textarea).font;
      measureDiv.style.padding = window.getComputedStyle(textarea).padding;
      measureDiv.style.fontFamily =
        "ui-monospace, SFMono-Regular, 'SF Mono', Consolas, 'Liberation Mono', Menlo, monospace";
      measureDiv.style.fontSize = "0.875rem";
      measureDiv.style.lineHeight = "1.7";
      document.body.appendChild(measureDiv);

      // Mesurer la largeur du texte jusqu'au curseur sur la ligne actuelle
      const lineText = lines[currentLine] || "";
      measureDiv.textContent = lineText;
      const textWidth = measureDiv.offsetWidth;

      // Nettoyer
      document.body.removeChild(measureDiv);

      // Obtenir la position du textarea dans le viewport
      const textareaRect = textarea.getBoundingClientRect();

      // Calculer la position du curseur
      // Padding du textarea (py-3 = 0.75rem = 12px, pl-14 = 3.5rem = 56px)
      const paddingTop = 12;
      const paddingLeft = 0; // pl-14 = 3.5rem = 56px (pour les numéros de lignes)

      // Calculer la position verticale (basée sur le numéro de ligne)
      const lineHeight = parseFloat(getComputedStyle(textarea).lineHeight);
      const topPosition =
        textareaRect.top + paddingTop + currentLine * lineHeight;

      // Calculer la position horizontale (basée sur la colonne)
      // Le textarea a un paddingLeft pour les numéros de lignes
      const leftPosition = textareaRect.left + paddingLeft + textWidth;

      // Ajuster pour éviter que le popover sorte de l'écran
      const popoverWidth = 320; // min-w-80 = 20rem = 320px
      const popoverHeight = 320; // max-h-80 = 20rem = 320px
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let finalLeft = leftPosition;
      let finalTop = topPosition + lineHeight; // Positionner juste sous la ligne du curseur

      // Si le popover dépasse à droite, l'aligner à gauche du curseur
      if (finalLeft + popoverWidth > viewportWidth) {
        finalLeft = leftPosition - popoverWidth;
      }

      // Si le popover dépasse en bas, l'afficher au-dessus du curseur
      if (finalTop + popoverHeight > viewportHeight) {
        finalTop = topPosition - popoverHeight;
      }

      // Si le popover dépasse à gauche, l'aligner au début de la ligne
      if (finalLeft < textareaRect.left) {
        finalLeft = textareaRect.left + paddingLeft;
      }

      // Si le popover dépasse en haut, l'aligner en haut
      if (finalTop < textareaRect.top) {
        finalTop = textareaRect.top + paddingTop;
      }

      setCompletionPosition({
        top: finalTop,
        left: finalLeft,
      });
    },
    [value],
  );

  // Fonction pour synchroniser le scroll entre textarea, zone de coloration et numéros de lignes
  const handleScroll = useCallback(() => {
    const textarea = textareaRef.current;
    const highlight = highlightRef.current;
    const lineNumbers = lineNumbersRef.current;
    if (textarea && highlight) {
      highlight.scrollTop = textarea.scrollTop;
      highlight.scrollLeft = textarea.scrollLeft;
    }
    if (textarea && lineNumbers) {
      // Synchroniser uniquement le scroll vertical pour les numéros de lignes
      lineNumbers.scrollTop = textarea.scrollTop;
    }
    // Mettre à jour la position du popover lors du scroll
    if (showCompletions && textarea) {
      updateCompletionPosition(textarea);
    }
  }, [showCompletions, updateCompletionPosition]);

  // Fonctions pour gérer l'historique
  const addToHistory = useCallback(
    (newValue: string) => {
      if (isUndoRedo) return; // Ne pas ajouter à l'historique lors d'undo/redo

      setHistory((prevHistory) => {
        const newHistory = prevHistory.slice(0, historyIndex + 1);
        newHistory.push(newValue);

        // Limiter l'historique à 50 entrées pour éviter les problèmes de mémoire
        if (newHistory.length > 50) {
          newHistory.shift();
          return newHistory;
        }

        return newHistory;
      });

      setHistoryIndex((prevIndex) => {
        const newIndex = prevIndex + 1;
        return Math.min(newIndex, 49); // Ajuster l'index si on a limité l'historique
      });
    },
    [historyIndex, isUndoRedo],
  );

  const undo = useCallback(() => {
    if (historyIndex > 0) {
      setIsUndoRedo(true);
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      onChange(history[newIndex]);
      setTimeout(() => setIsUndoRedo(false), 0);
    }
  }, [historyIndex, history, onChange]);

  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      setIsUndoRedo(true);
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      onChange(history[newIndex]);
      setTimeout(() => setIsUndoRedo(false), 0);
    }
  }, [historyIndex, history, onChange]);

  // Combiner la base commune avec les extensions (dédupliqué par label)
  const fullCompletionDatabase = useMemo(
    () => [
      ...completionDatabase,
      ...extendedCompletionDatabase.filter(
        (ext) => !completionDatabase.some((base) => base.label === ext.label),
      ),
    ],
    [],
  );

  // Patterns étendus pour l'éditeur complet (inclut les commentaires et mots-clés de structure)
  const extendedSyntaxPatterns = useMemo(() => {
    const basePatterns = getSyntaxPatterns(silaeVariables);

    // Ajouter les patterns spécifiques à l'éditeur complet
    return [
      // Commentaires (doivent être en premier pour éviter la coloration des autres éléments)
      {
        pattern: /(\/\/.*$)/gm,
        type: "comments",
      },
      // Mots-clés de structure
      {
        pattern: /\b(Begin|End)\b/gi,
        type: "structure",
      },
      // Mots-clés de contrôle
      {
        pattern: /\b(If|Then|Else|Endif|SelectCase|Case|Default|EndSelect)\b/gi,
        type: "control",
      },
      // Mot-clé "call" (seulement s'il n'est pas suivi d'une fonction - géré par le pattern dans basePatterns)
      {
        pattern: /\bcall\b(?!\s+[A-Za-z_][A-Za-z0-9_]*)/gi,
        type: "control",
      },
      // Ajouter les patterns de base
      ...basePatterns,
    ];
  }, [silaeVariables]);

  // Fonction pour appliquer la coloration syntaxique
  const applySyntaxHighlightingCallback = useCallback(
    (text: string): React.ReactElement[] => {
      const elements = applySyntaxHighlighting(
        text,
        syntaxColors,
        extendedSyntaxPatterns,
      );
      return elements.map((element) => (
        <span key={element.key} className={element.className}>
          {element.text}
        </span>
      ));
    },
    [extendedSyntaxPatterns],
  );

  // Fonction pour filtrer les suggestions (utilise la base de données étendue)
  const getFilteredCompletionsCallback = useCallback(
    (
      word: string,
      isVariableContext: boolean,
      isCallContext: boolean,
      prefix: string,
    ): CompletionItem[] => {
      // Utiliser la fonction commune avec la base de données étendue et les variables Silae
      const commonCompletions = getFilteredCompletions(
        word,
        isVariableContext,
        isCallContext,
        prefix,
        silaeVariables,
      );

      // Si pas de contexte spécial, utiliser la base de données étendue
      if (!isVariableContext && !isCallContext && word.length >= 1) {
        return fullCompletionDatabase.filter(
          (item) =>
            item.type !== "variable" && // Exclure les variables de la liste principale
            item.label.toLowerCase().includes(word.toLowerCase()),
        );
      }

      return commonCompletions;
    },
    [silaeVariables, fullCompletionDatabase],
  );

  // Fonction pour insérer une suggestion avec indentation automatique
  const insertCompletionCallback = (
    completion: CompletionItem,
    wordStart: number,
    wordEnd: number,
    isCallContext: boolean = false,
  ) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const { newValue, newCursorPosition } = insertCompletion(
      completion,
      wordStart,
      wordEnd,
      value,
      isCallContext,
    );

    // Marquer que c'est une insertion de suggestion
    setIsCompletionInsertion(true);

    onChange(newValue);

    // Ajouter à l'historique immédiatement après l'insertion d'une suggestion
    addToHistory(newValue);

    // Réinitialiser le flag après un court délai
    setTimeout(() => setIsCompletionInsertion(false), 0);

    setShowCompletions(false);

    // Positionner le curseur
    setTimeout(() => {
      textarea.selectionStart = textarea.selectionEnd = newCursorPosition;
      textarea.focus();
    }, 0);
  };

  // Mettre à jour la coloration quand le contenu change
  useEffect(() => {
    setHighlightedContent(applySyntaxHighlightingCallback(value));
  }, [value, applySyntaxHighlightingCallback]);

  // Synchroniser l'historique quand la valeur change de l'extérieur (chargement de fichier, etc.)
  useEffect(() => {
    // Vérifier si la valeur a changé de l'extérieur (pas par l'utilisateur)
    if (
      value !== history[historyIndex] &&
      !isUndoRedo &&
      !isCompletionInsertion
    ) {
      // Réinitialiser l'historique avec la nouvelle valeur
      setHistory([value]);
      setHistoryIndex(0);

      // Forcer la mise à jour du textarea si nécessaire
      if (textareaRef.current && textareaRef.current.value !== value) {
        textareaRef.current.value = value;
        // Positionner le curseur au début
        textareaRef.current.selectionStart = 0;
        textareaRef.current.selectionEnd = 0;
      }
    }
  }, [value, history, historyIndex, isUndoRedo, isCompletionInsertion]);

  // Nettoyer le timeout lors du démontage
  useEffect(() => {
    return () => {
      if (historyTimeoutRef.current) {
        clearTimeout(historyTimeoutRef.current);
      }
    };
  }, []);

  // Mettre à jour la position du popover quand le curseur bouge et que les suggestions sont affichées
  useEffect(() => {
    if (showCompletions && textareaRef.current) {
      const textarea = textareaRef.current;
      const handleCursorMove = () => {
        if (textarea) {
          updateCompletionPosition(textarea);
        }
      };

      // Mettre à jour la position immédiatement
      handleCursorMove();

      // Utiliser un intervalle pour surveiller les changements de position du curseur
      const intervalId = setInterval(() => {
        if (textarea && document.activeElement === textarea) {
          handleCursorMove();
        }
      }, 100); // Vérifier toutes les 100ms

      // Écouter les événements de mouvement du curseur
      textarea.addEventListener("keyup", handleCursorMove);
      textarea.addEventListener("click", handleCursorMove);
      textarea.addEventListener("mousedown", handleCursorMove);

      return () => {
        clearInterval(intervalId);
        textarea.removeEventListener("keyup", handleCursorMove);
        textarea.removeEventListener("click", handleCursorMove);
        textarea.removeEventListener("mousedown", handleCursorMove);
      };
    }
  }, [showCompletions, value, updateCompletionPosition]);

  // Gérer les changements de contenu
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    let newValue = e.target.value;
    const textarea = e.target;
    const cursorPosition = textarea.selectionStart;

    // Ajuster automatiquement l'indentation des mots-clés de fermeture
    // (seulement si on vient de taper un espace après un mot-clé de fermeture)
    const lastChar = newValue[cursorPosition - 1];
    if (lastChar === " " || lastChar === "\n") {
      const adjustedValue = adjustClosingKeywordIndentation(
        newValue,
        cursorPosition,
      );
      if (adjustedValue !== newValue) {
        newValue = adjustedValue;
        // Mettre à jour la valeur dans le textarea
        setTimeout(() => {
          if (textareaRef.current) {
            textareaRef.current.value = newValue;
            textareaRef.current.selectionStart =
              textareaRef.current.selectionEnd = cursorPosition;
          }
        }, 0);
      }
    }

    onChange(newValue);

    // Ne pas ajouter à l'historique si c'est une insertion de suggestion
    if (isCompletionInsertion) {
      return;
    }

    // Nettoyer le timeout précédent
    if (historyTimeoutRef.current) {
      clearTimeout(historyTimeoutRef.current);
    }

    // Ajouter à l'historique (avec un délai pour éviter trop d'entrées)
    historyTimeoutRef.current = setTimeout(() => {
      addToHistory(newValue);
    }, 500); // Délai de 500ms pour regrouper les modifications rapides

    // Gérer l'auto-complétion
    const { word, isVariableContext, isCallContext, prefix } = getCurrentWord(
      newValue,
      cursorPosition,
    );

    // Déclencher l'auto-complétion si on a un mot OU si on est dans un contexte "call"
    if (word.length >= 1 || isCallContext) {
      const filteredCompletions = getFilteredCompletionsCallback(
        word,
        isVariableContext,
        isCallContext,
        prefix,
      );
      if (filteredCompletions.length > 0) {
        setCompletions(filteredCompletions);
        setSelectedCompletionIndex(0);
        setShowCompletions(true);
        // Calculer la position du popover près du curseur
        setTimeout(() => {
          if (textareaRef.current) {
            updateCompletionPosition(textareaRef.current);
          }
        }, 0);
      } else {
        setShowCompletions(false);
        setCompletionPosition(null);
      }
    } else {
      setShowCompletions(false);
      setCompletionPosition(null);
    }
  };

  // Gérer les tabulations et la navigation dans les suggestions
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Gérer les raccourcis undo/redo
    if (e.ctrlKey || e.metaKey) {
      switch (e.key) {
        case "z":
          e.preventDefault();
          if (e.shiftKey) {
            // Ctrl+Shift+Z ou Cmd+Shift+Z pour redo
            redo();
          } else {
            // Ctrl+Z ou Cmd+Z pour undo
            undo();
          }
          return;
        case "y":
          e.preventDefault();
          // Ctrl+Y ou Cmd+Y pour redo
          redo();
          return;
      }
    }

    // Gérer l'auto-complétion
    if (showCompletions && completions.length > 0) {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedCompletionIndex((prev) =>
            prev < completions.length - 1 ? prev + 1 : 0,
          );
          return;

        case "ArrowUp":
          e.preventDefault();
          setSelectedCompletionIndex((prev) =>
            prev > 0 ? prev - 1 : completions.length - 1,
          );
          return;

        case "Enter":
        case "Tab": {
          e.preventDefault();
          const selectedCompletion = completions[selectedCompletionIndex];
          const cursorPosition = textarea.selectionStart;
          const { start, end, isCallContext } = getCurrentWord(
            value,
            cursorPosition,
          );
          insertCompletionCallback(
            selectedCompletion,
            start,
            end,
            isCallContext,
          );
          return;
        }

        case "Escape":
          e.preventDefault();
          setShowCompletions(false);
          return;
      }
    }

    // Gérer l'auto-indentation intelligente pour la touche Entrée
    if (e.key === "Enter" && !showCompletions) {
      e.preventDefault();

      const cursorPosition = textarea.selectionStart;
      const { newValue, newCursorPosition } = handleEnterKey(
        value,
        cursorPosition,
      );

      onChange(newValue);

      // Positionner le curseur après l'indentation
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = newCursorPosition;
      }, 0);

      return;
    }

    // Gérer les tabulations normales
    if (e.key === "Tab") {
      e.preventDefault();
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const { newValue, newCursorPosition } = handleTabKey(value, start, end);
      onChange(newValue);

      // Restaurer la position du curseur
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = newCursorPosition;
      }, 0);
    }
  };

  const getVariableIcon = (type: SilaeVariableType) => {
    switch (type) {
      case "function":
        return <FunctionSquare className="h-6 w-6" />;

      case "variable":
        return <Variable className="h-6 w-6" />;
    }
  };

  return (
    <div
      className={cn(
        "relative flex flex-col bg-background border border-border rounded-lg shadow-xl min-h-[800px] ",
        className,
      )}
    >
      {/* Barre d'outils IDE */}
      <div className="flex items-center justify-between px-4 py-2 bg-muted border-b border-border">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
            <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
          </div>
          <span className="text-sm text-foreground font-medium">
            {t("silaeCodeEditor.title")}
          </span>
        </div>
        {!isInDialog && (
          <TooltipProvider>
            <div className="flex items-center gap-2 h-6">
              {/* Outils d'exécution */}
              <div className="flex items-center gap-1 px-2 py-1 bg-muted/30 rounded">
                <Tooltip
                  content={t("silaeCodeEditor.tooltips.loadExample")}
                  placement="top"
                >
                  <button
                    onClick={handleLoadExample}
                    className="p-1 rounded hover:bg-muted/50 transition-colors cursor-pointer"
                  >
                    <FileText className="h-4 w-4" />
                  </button>
                </Tooltip>
              </div>

              <div className="w-px h-4 bg-border"></div>

              {/* Outils de fichier */}
              {hasFileManagement && (
                <div className="flex items-center gap-1 px-2 py-1 bg-muted/30 rounded">
                  <Tooltip
                    content={t("silaeCodeEditor.tooltips.saveFile")}
                    placement="top"
                  >
                    <button
                      onClick={handleSaveCode}
                      className="p-1 rounded hover:bg-muted/50 transition-colors cursor-pointer"
                    >
                      <Save className="h-4 w-4" />
                    </button>
                  </Tooltip>

                  <Tooltip
                    content={t("silaeCodeEditor.tooltips.loadFile")}
                    placement="top"
                  >
                    <label className="p-1 rounded hover:bg-muted/50 transition-colors cursor-pointer">
                      <Upload className="h-4 w-4" />
                      <input
                        type="file"
                        accept=".txt,.silae,.code"
                        onChange={onLoadFile}
                        className="hidden"
                      />
                    </label>
                  </Tooltip>
                </div>
              )}

              <div className="w-px h-4 bg-border"></div>

              {/* Outils d'édition */}
              <div className="flex items-center gap-1 px-2 py-1 bg-muted/30 rounded">
                <Tooltip
                  content={t("silaeCodeEditor.tooltips.undo")}
                  placement="top"
                >
                  <button
                    onClick={undo}
                    disabled={historyIndex <= 0}
                    className="p-1 rounded hover:bg-muted/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  >
                    <Undo className="h-4 w-4" />
                  </button>
                </Tooltip>

                <Tooltip
                  content={t("silaeCodeEditor.tooltips.redo")}
                  placement="top"
                >
                  <button
                    onClick={redo}
                    disabled={historyIndex >= history.length - 1}
                    className="p-1 rounded hover:bg-muted/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  >
                    <Redo className="h-4 w-4" />
                  </button>
                </Tooltip>

                <Tooltip
                  content={t("silaeCodeEditor.tooltips.copyCode")}
                  placement="top"
                >
                  <button
                    onClick={handleCopyCode}
                    className="p-1 rounded hover:bg-muted/50 transition-colors cursor-pointer"
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                </Tooltip>

                <Tooltip
                  content={t("silaeCodeEditor.tooltips.clearCode")}
                  placement="top"
                >
                  <button
                    onClick={handleClearCode}
                    className="p-1 rounded hover:bg-muted/50 transition-colors text-red-600 hover:text-red-700 cursor-pointer"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </Tooltip>
              </div>

              <div className="w-px h-4 bg-border"></div>

              {/* Nom de fichier */}
              {onFileNameChange && (
                <>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={defaultFileName}
                      onChange={(e) => onFileNameChange(e.target.value)}
                      className="px-2 py-1 text-xs bg-background border border-border rounded text-foreground min-w-32"
                      placeholder={t("silaeCodeEditor.fileNamePlaceholder")}
                    />
                  </div>
                  <div className="w-px h-4 bg-border"></div>
                </>
              )}

              {/* Statistiques */}
              <div className="flex items-center gap-2">
                <Tooltip
                  content={t("silaeCodeEditor.tooltips.lineCount")}
                  placement="top"
                >
                  <div className="text-xs text-muted-foreground px-2 py-1 bg-muted/50 rounded cursor-help">
                    {value.split("\n").length}{" "}
                    {t("silaeCodeEditor.stats.lines")}
                  </div>
                </Tooltip>

                <Tooltip
                  content={t("silaeCodeEditor.tooltips.charCount")}
                  placement="top"
                >
                  <div className="text-xs text-muted-foreground px-2 py-1 bg-muted/50 rounded cursor-help">
                    {value.length} {t("silaeCodeEditor.stats.characters")}
                  </div>
                </Tooltip>
              </div>
            </div>
          </TooltipProvider>
        )}
      </div>

      {/* Zone d'édition principale */}
      <div className="relative flex-1 h-full flex-grow overflow-hidden">
        {/* Numérotation des lignes */}
        <div
          ref={lineNumbersRef}
          className="absolute left-0 top-0 bottom-0 w-12 bg-muted border-r border-border text-muted-foreground font-mono overflow-hidden"
          style={{
            fontFamily:
              "ui-monospace, SFMono-Regular, 'SF Mono', Consolas, 'Liberation Mono', Menlo, monospace",
            fontSize: "0.875rem",
          }}
        >
          <div className="py-3">
            {value.split("\n").map((_, index) => (
              <div
                key={index}
                className="flex items-center justify-center px-1"
                style={{
                  lineHeight: "1.7",
                }}
              >
                {index + 1}
              </div>
            ))}
          </div>
        </div>

        {/* Zone de coloration syntaxique (arrière-plan) */}
        <div
          ref={highlightRef}
          className="absolute inset-0 pl-14 pr-4 py-3 font-mono text-sm whitespace-pre-wrap break-words pointer-events-none overflow-hidden text-foreground"
          style={{
            fontFamily:
              "ui-monospace, SFMono-Regular, 'SF Mono', Consolas, 'Liberation Mono', Menlo, monospace",
            lineHeight: "1.7",
          }}
        >
          {highlightedContent}
        </div>

        {/* Zone de saisie (transparente par-dessus) */}
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onScroll={handleScroll}
          placeholder={defaultPlaceholder}
          readOnly={readOnly}
          className={cn(
            "absolute inset-0 w-full h-full pl-14 pr-4 py-3 font-mono text-sm bg-transparent text-transparent caret-foreground resize-none outline-none overflow-auto",
            readOnly && "cursor-not-allowed opacity-50",
          )}
          style={{
            fontFamily:
              "ui-monospace, SFMono-Regular, 'SF Mono', Consolas, 'Liberation Mono', Menlo, monospace",
            lineHeight: "1.7",
          }}
          spellCheck={false}
        />

        {/* Popup d'auto-complétion amélioré - Rendu via portal pour échapper aux limites du dialog */}
        {showCompletions &&
          completions.length > 0 &&
          completionPosition &&
          typeof window !== "undefined" &&
          createPortal(
            <div
              className="fixed z-[9999] bg-popover border border-border rounded-lg shadow-2xl max-h-80 overflow-y-auto min-w-80 max-w-96 backdrop-blur-sm"
              style={{
                top: `${completionPosition.top}px`,
                left: `${completionPosition.left}px`,
              }}
            >
              <div className="p-2 border-b border-border">
                <div className="text-xs text-muted-foreground font-medium">
                  {t("silaeCodeEditor.suggestions.title", {
                    count: completions.length,
                  })}
                </div>
              </div>
              {completions.map((completion, index) => (
                <div
                  key={`${completion.label}-${index}`}
                  className={cn(
                    "px-3 py-2 cursor-pointer border-b border-border last:border-b-0 transition-colors",
                    {
                      "bg-accent": index === selectedCompletionIndex,
                      "hover:bg-muted": index !== selectedCompletionIndex,
                    },
                  )}
                  onClick={() => {
                    const textarea = textareaRef.current;
                    if (textarea) {
                      const cursorPosition = textarea.selectionStart;
                      const { start, end, isCallContext } = getCurrentWord(
                        value,
                        cursorPosition,
                      );
                      insertCompletionCallback(
                        completion,
                        start,
                        end,
                        isCallContext,
                      );
                    }
                  }}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground">
                      {getVariableIcon(completion.type as SilaeVariableType)}
                    </span>
                    <div>
                      <span className="font-mono text-sm text-foreground">
                        {completion.label}
                      </span>
                      {completion.description && (
                        <div className="text-xs text-muted-foreground">
                          {completion.description}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              <div className="p-2 border-t border-border">
                <div className="text-xs text-muted-foreground">
                  {t("silaeCodeEditor.suggestions.instructions")}
                </div>
              </div>
            </div>,
            document.body,
          )}
      </div>

      {/* Barre de statut */}
      <div className="flex items-center justify-between px-4 py-2 bg-muted border-t border-border text-xs text-muted-foreground">
        <div className="flex items-center gap-4">
          <span>SILAE</span>
          <span>UTF-8</span>
          <span>LF</span>
        </div>
        <div className="flex items-center gap-4">
          <span>{t("silaeCodeEditor.status.author")} :</span>
          <span>
            {user?.userType === "individual"
              ? `${user.firstName} ${user.lastName}`
              : user?.companyName}
          </span>
        </div>
      </div>
    </div>
  );
};
